import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Phone, ShieldCheck } from "lucide-react";
import { SERVICE_LOCAL_PAGES } from "@/data/servicelocalpages";
import { CONTACT, SITE } from "@/data/content";
import { Words, Reveal } from "@/lib/motion";
import Magnetic from "@/components/Magnetic";
import Seo from "@/components/Seo";
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export default function ServiceCityPage({ pageKey }) {
    const d = SERVICE_LOCAL_PAGES[pageKey];

    useEffect(() => {
        window.scrollTo(0, 0);
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    }, [pageKey]);

    if (!d) return null;

    return (
        <main data-testid={`service-city-page-${pageKey}`}>
            <Seo
                title={d.metaTitle}
                description={d.metaDesc}
                canonical={`${SITE.url}/${d.slug}`}
                image={d.heroImg}
                noindex={pageKey !== "desinfection-strasbourg"}
                jsonLd={[
                    {
                        "@context": "https://schema.org",
                        "@type": "Service",
                        serviceType: `${d.serviceType} à ${d.city}`,
                        name: `${d.serviceLabel} à ${d.city}`,
                        description: d.metaDesc,
                        url: `${SITE.url}/${d.slug}`,
                        areaServed: { "@type": "City", name: d.city },
                        provider: {
                            "@type": "LocalBusiness",
                            name: SITE.name,
                            telephone: SITE.phoneIntl,
                            url: SITE.url + "/",
                            image: SITE.url + "/logo-ign.png",
                        },
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        mainEntity: d.faq.map((f) => ({
                            "@type": "Question",
                            name: f.q,
                            acceptedAnswer: { "@type": "Answer", text: f.a },
                        })),
                    },
                    {
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        itemListElement: [
                            { "@type": "ListItem", position: 1, name: "Accueil", item: SITE.url + "/" },
                            { "@type": "ListItem", position: 2, name: d.serviceLabel, item: `${SITE.url}${d.parentLink}` },
                            { "@type": "ListItem", position: 3, name: `${d.serviceLabel} à ${d.city}`, item: `${SITE.url}/${d.slug}` },
                        ],
                    },
                ]}
            />

            {/* HERO */}
            <section className="relative min-h-[70vh] flex items-center overflow-hidden bg-ink">
                <div className="absolute inset-0 z-0">
                    <img src={d.heroImg} alt={`${d.serviceLabel} à ${d.city}`} className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 z-[1]" style={{ background: "linear-gradient(180deg, rgba(6,21,41,0.82), rgba(6,21,41,0.72) 40%, rgba(6,21,41,0.92))" }} />
                <div className="absolute inset-0 z-[1] pointer-events-none" style={{ background: "radial-gradient(55% 50% at 20% 40%, rgba(46,123,255,0.25), transparent 70%)" }} />
                <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 pt-40 pb-24">
                    <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="inline-block text-[11px] uppercase tracking-[0.25em] text-electric-soft font-body mb-6 border border-white/20 px-4 py-1.5">
                        {d.kicker}
                    </motion.span>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-[1.1] mb-7 font-display">
                        <span className="block overflow-hidden"><Words text={d.title[0]} delay={0.3} mount /></span>
                        <span className="block overflow-hidden font-semibold"><Words text={d.title[1]} delay={0.45} mount /></span>
                    </h1>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.7 }} className="flex flex-wrap gap-4">
                        <Magnetic strength={0.3}>
                            <Link to="/#contact" className="group inline-flex items-center gap-3 bg-electric text-white px-8 py-4 text-sm uppercase tracking-wide font-semibold font-body hover:bg-white hover:text-navy transition-colors duration-300" data-testid="service-city-cta">
                                {d.ctaLabel} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </Magnetic>
                        <a href={`tel:${CONTACT.phone}`} className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 text-sm uppercase tracking-wide font-semibold font-body hover:bg-white hover:text-navy transition-colors duration-300">
                            <Phone className="w-4 h-4" /> {CONTACT.phoneDisplay}
                        </a>
                    </motion.div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-brand-red z-10" />
            </section>

            {/* INTRO */}
            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-4xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <span className="uppercase text-xs tracking-[0.2em] font-bold text-brand-red font-body">
                            {d.serviceLabel} à {d.city}
                        </span>
                    </Reveal>
                    {d.intro.map((p, i) => (
                        <Reveal key={i} delay={0.06 + i * 0.06}>
                            <p className={"text-base md:text-lg leading-relaxed font-body " + (i === 0 ? "text-slate-600 mt-6" : "text-slate-600 mt-5")}>{p}</p>
                        </Reveal>
                    ))}
                    <Reveal delay={0.2} className="flex flex-wrap gap-2.5 mt-8">
                        {d.chips.map((c) => (
                            <span key={c} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-mist border border-line text-navy font-body">
                                <Check className="w-4 h-4 text-electric" /> {c}
                            </span>
                        ))}
                    </Reveal>
                </div>
            </section>

            {/* WHY US */}
            <section className="py-20 bg-navy">
                <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
                    <Reveal>
                        <h2 className="text-2xl md:text-3xl font-medium text-white font-display mb-8">
                            Pourquoi choisir IGN Services Alsace à {d.city}
                        </h2>
                    </Reveal>
                    <div className="flex flex-wrap justify-center gap-3">
                        {["Exigence terrain", "Interlocuteur unique", "Contrôle qualité", "Réactivité 7j/7", "Produits professionnels", "Résultat visible"].map((e, i) => (
                            <Reveal key={e} delay={i * 0.05}>
                                <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-white/10 border border-white/15 text-white font-body">
                                    <ShieldCheck className="w-4 h-4 text-electric-soft" /> {e}
                                </span>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <Reveal className="text-center mb-10">
                        <span className="uppercase text-xs tracking-[0.2em] font-bold text-brand-red font-body">Questions fréquentes</span>
                        <h2 className="text-2xl md:text-3xl font-medium text-navy mt-4 font-display">{d.serviceLabel} à {d.city}</h2>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <Accordion type="single" collapsible className="w-full">
                            {d.faq.map((f, i) => (
                                <AccordionItem key={i} value={`item-${i}`}>
                                    <AccordionTrigger className="text-left font-display text-navy text-base">{f.q}</AccordionTrigger>
                                    <AccordionContent className="text-sm text-slate-600 font-body leading-relaxed">{f.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Reveal>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 bg-ink relative grain overflow-hidden">
                <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 60% at 50% 30%, rgba(46,123,255,0.2), transparent 70%)" }} />
                <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
                    <Reveal>
                        <h2 className="text-3xl md:text-5xl font-light text-white font-display mb-6">
                            {d.serviceLabel} à {d.city} ?
                        </h2>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <p className="text-white/70 font-body mb-10">Réponse sous 24h, gratuite et sans engagement.</p>
                    </Reveal>
                    <Reveal delay={0.12} className="flex flex-wrap justify-center gap-4">
                        <Magnetic strength={0.35}>
                            <Link to="/#contact" className="inline-flex items-center gap-3 bg-electric text-white px-9 py-4 text-sm uppercase tracking-wide font-semibold font-body hover:bg-white hover:text-navy transition-colors duration-300" style={{ boxShadow: "0 0 34px rgba(46,123,255,0.45)" }}>
                                {d.ctaLabel} <ArrowRight className="w-4 h-4" />
                            </Link>
                        </Magnetic>
                        <a href={`tel:${CONTACT.phone}`} className="inline-flex items-center gap-2 border border-white/40 text-white px-9 py-4 text-sm uppercase tracking-wide font-semibold font-body hover:bg-white hover:text-navy transition-colors duration-300">
                            <Phone className="w-4 h-4" /> {CONTACT.phoneDisplay}
                        </a>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <Link to={d.parentLink} className="inline-block mt-10 text-sm text-electric-soft hover:text-white transition-colors font-body">
                            &larr; {d.parentLabel}
                        </Link>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
